import {
  cleJour,
  estSuite,
  joursCouverts,
  plageDuJour as plageDuJourPartage,
} from '@imp/shared/agenda-jours';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Handshake,
  HardHat,
  type LucideIcon,
  MapPin,
  Package,
  Phone,
  Plus,
  Ruler,
  Search,
  Shapes,
  Trash2,
  Truck,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  createEquipe,
  createEvenement,
  createTypeEvenement,
  deleteEquipe,
  deleteEvenement,
  deleteTypeEvenement,
  type Equipe,
  type EquipeMembre,
  type EtatSyncGoogle,
  type Evenement,
  EVENEMENT_STATUT_LABELS,
  type EvenementInput,
  listEquipes,
  listEtatsSyncGoogle,
  listEvenements,
  listMembres,
  listTypesEvenement,
  setEmployeEquipe,
  type TypeEvenement,
  updateEquipe,
  updateEvenement,
  updateTypeEvenement,
} from '@/modules/agenda/api';
import { listChantiers, listEmployes } from '@/modules/chantiers/api';
import { ChoixClient } from '@/modules/clients/ChoixClient';
import { listContacts } from '@/modules/clients/api';

type Vue = 'jour' | 'semaine' | 'mois';
const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Icônes-formes par type d'événement (clé = `types_evenement.icone`).
// La forme distingue le type INDÉPENDAMMENT de la couleur (qui peut être celle
// de la personne ou de l'équipe assignée) — l'agenda reste lisible d'un coup d'œil.
const TYPE_ICONES: Record<string, LucideIcon> = {
  search: Search,
  'clipboard-check': ClipboardCheck,
  handshake: Handshake,
  ruler: Ruler,
  wrench: Wrench,
  'hard-hat': HardHat,
  truck: Truck,
  package: Package,
  'map-pin': MapPin,
  phone: Phone,
  calendar: CalendarClock,
};
function iconeForType(name: string | null): LucideIcon {
  return (name ? TYPE_ICONES[name] : undefined) ?? CalendarClock;
}

// Icônes proposées dans le sélecteur d'icône (admin des types d'événement).
const ICONE_KEYS: readonly string[] = [
  'search',
  'clipboard-check',
  'handshake',
  'ruler',
  'wrench',
  'hard-hat',
  'truck',
  'package',
  'map-pin',
  'phone',
  'calendar',
];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
const dateKey = cleJour;
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(d.getDate() + n);
  return r;
}
function startOfWeek(d: Date): Date {
  const offset = (d.getDay() + 6) % 7; // 0 = lundi
  return addDays(new Date(d.getFullYear(), d.getMonth(), d.getDate()), -offset);
}
function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
/** Ce qu'occupe l'événement sur le jour affiché — voir @imp/shared/agenda-jours. */
function plageDuJour(e: Evenement, jour: string): string {
  return plageDuJourPartage(e, jour, hhmm);
}
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

interface EditState {
  id?: string;
  type_id: string;
  titre: string;
  statut: string;
  debut: string;
  fin: string;
  journee_entiere: boolean;
  contact_id: string;
  chantier_id: string;
  assigne: string; // '' | `emp:<id>` | `eq:<id>`
  adresse: string;
  description: string;
}

export function AgendaPage() {
  const queryClient = useQueryClient();
  const [vue, setVue] = useState<Vue>('mois');
  const [cursor, setCursor] = useState(() => new Date());
  const [form, setForm] = useState<EditState | null>(null);
  const [gestionEquipes, setGestionEquipes] = useState(false);
  const [gestionTypes, setGestionTypes] = useState(false);

  const { days, rangeStart, rangeEnd, label } = useMemo(() => {
    if (vue === 'jour') {
      const d = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
      return {
        days: [d],
        rangeStart: d.toISOString(),
        rangeEnd: addDays(d, 1).toISOString(),
        label: d.toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
      };
    }
    if (vue === 'semaine') {
      const start = startOfWeek(cursor);
      const ds = Array.from({ length: 7 }, (_, i) => addDays(start, i));
      const end = addDays(start, 6);
      const sameMonth = start.getMonth() === end.getMonth();
      const lbl = sameMonth
        ? `${start.getDate()} – ${end.getDate()} ${end.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
        : `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      return {
        days: ds,
        rangeStart: start.toISOString(),
        rangeEnd: addDays(start, 7).toISOString(),
        label: lbl,
      };
    }
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = startOfWeek(monthStart);
    const ds = Array.from({ length: 42 }, (_, i) => addDays(start, i));
    return {
      days: ds,
      rangeStart: start.toISOString(),
      rangeEnd: addDays(start, 42).toISOString(),
      label: cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
    };
  }, [cursor, vue]);

  const eventsQ = useQuery({
    queryKey: ['evenements', rangeStart, rangeEnd],
    queryFn: () => listEvenements(rangeStart, rangeEnd),
  });
  const typesQ = useQuery({ queryKey: ['types_evenement'], queryFn: listTypesEvenement });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const chantiersQ = useQuery({ queryKey: ['chantiers'], queryFn: listChantiers });
  const employesQ = useQuery({ queryKey: ['employes'], queryFn: listEmployes });
  const equipesQ = useQuery({ queryKey: ['equipes'], queryFn: listEquipes });

  const typeMap = new Map((typesQ.data ?? []).map((t) => [t.id, t]));
  const empMap = new Map((employesQ.data ?? []).map((e) => [e.id, e]));
  const equipeMap = new Map((equipesQ.data ?? []).map((e) => [e.id, e]));
  const eventsByDay = new Map<string, Evenement[]>();
  for (const e of eventsQ.data ?? []) {
    for (const k of joursCouverts(e.debut_at, e.fin_at)) {
      const arr = eventsByDay.get(k) ?? [];
      arr.push(e);
      eventsByDay.set(k, arr);
    }
  }
  // Tri fait ici, une fois pour toutes : les trois vues affichent le même
  // ordre, et un événement entamé la veille arrive en tête de journée.
  for (const arr of eventsByDay.values()) arr.sort((a, b) => a.debut_at.localeCompare(b.debut_at));
  const couleurOf = (e: Evenement): string =>
    (e.employe_id ? empMap.get(e.employe_id)?.couleur_agenda : null) ??
    (e.equipe_id ? equipeMap.get(e.equipe_id)?.couleur : null) ??
    (e.type_id ? typeMap.get(e.type_id)?.couleur : null) ??
    '#64748b';

  const toInput = (s: EditState): EvenementInput => ({
    type_id: s.type_id === '' ? null : s.type_id,
    titre: s.titre,
    description: s.description.trim() === '' ? null : s.description,
    debut_at: fromLocalInput(s.debut),
    fin_at: fromLocalInput(s.fin),
    journee_entiere: s.journee_entiere,
    statut: s.statut,
    contact_id: s.contact_id === '' ? null : s.contact_id,
    chantier_id: s.chantier_id === '' ? null : s.chantier_id,
    employe_id: s.assigne.startsWith('emp:') ? s.assigne.slice(4) : null,
    equipe_id: s.assigne.startsWith('eq:') ? s.assigne.slice(3) : null,
    adresse: s.adresse.trim() === '' ? null : s.adresse,
  });

  const saveMut = useMutation({
    mutationFn: async (s: EditState) => {
      if (s.id) await updateEvenement(s.id, toInput(s));
      else await createEvenement(toInput(s));
    },
    onSuccess: () => {
      setForm(null);
      void queryClient.invalidateQueries({ queryKey: ['evenements'] });
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteEvenement(id),
    onSuccess: () => {
      setForm(null);
      void queryClient.invalidateQueries({ queryKey: ['evenements'] });
    },
  });

  const openNew = (day?: Date) => {
    const base = day ?? new Date();
    const d = `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`;
    setForm({
      type_id: '',
      titre: '',
      statut: 'planifie',
      debut: `${d}T09:00`,
      fin: `${d}T10:00`,
      journee_entiere: false,
      contact_id: '',
      chantier_id: '',
      assigne: '',
      adresse: '',
      description: '',
    });
  };
  const openEdit = (e: Evenement) => {
    setForm({
      id: e.id,
      type_id: e.type_id ?? '',
      titre: e.titre,
      statut: e.statut,
      debut: toLocalInput(e.debut_at),
      fin: toLocalInput(e.fin_at),
      journee_entiere: e.journee_entiere,
      contact_id: e.contact_id ?? '',
      chantier_id: e.chantier_id ?? '',
      assigne: e.employe_id ? `emp:${e.employe_id}` : e.equipe_id ? `eq:${e.equipe_id}` : '',
      adresse: e.adresse ?? '',
      description: e.description ?? '',
    });
  };

  const shift = (dir: number) => {
    if (vue === 'jour') setCursor(addDays(cursor, dir));
    else if (vue === 'semaine') setCursor(addDays(cursor, dir * 7));
    else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
  };

  const todayKey = dateKey(new Date());

  function EventChip({ e, jour, compact }: { e: Evenement; jour: string; compact?: boolean }) {
    const type = e.type_id ? typeMap.get(e.type_id) : null;
    const Icone = iconeForType(type?.icone ?? null);
    // Un événement de plusieurs jours n'affiche son heure que le premier jour ;
    // les suivants portent « ↳ », pour ne pas faire croire à un nouveau RDV.
    const suite = estSuite(e.debut_at, e.fin_at, jour);
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={(ev) => {
          ev.stopPropagation();
          openEdit(e);
        }}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') {
            ev.stopPropagation();
            openEdit(e);
          }
        }}
        className={`flex cursor-pointer items-center gap-1 overflow-hidden rounded px-1.5 py-0.5 text-[11px] font-medium text-white ${compact ? '' : 'mb-1'}`}
        style={{ backgroundColor: couleurOf(e) }}
        title={`${type?.nom ? `${type.nom} — ` : ''}${e.titre} (${plageDuJour(e, jour)})`}
      >
        <Icone className="h-3 w-3 shrink-0" />
        <span className="min-w-0 truncate">
          {suite ? '↳ ' : e.journee_entiere ? '' : `${hhmm(e.debut_at)} `}
          {e.titre}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight first-letter:uppercase">{label}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex h-12 overflow-hidden rounded-md border border-input bg-card lg:h-11">
            {(['jour', 'semaine', 'mois'] as Vue[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => {
                  setVue(v);
                }}
                className={`px-4 text-base font-semibold capitalize ${vue === v ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
              >
                {v}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setCursor(new Date());
            }}
          >
            Aujourd'hui
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Précédent"
            onClick={() => {
              shift(-1);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Suivant"
            onClick={() => {
              shift(1);
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setGestionTypes(true);
            }}
          >
            <Shapes className="h-4 w-4" /> Types
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setGestionEquipes(true);
            }}
          >
            <Users className="h-4 w-4" /> Équipes
          </Button>
          <Button
            onClick={() => {
              openNew(vue === 'mois' ? undefined : days[0]);
            }}
          >
            <Plus className="h-4 w-4" /> Nouvel événement
          </Button>
        </div>
      </div>

      {vue === 'mois' ? (
        <div className="overflow-hidden rounded-lg border bg-card">
          <div className="grid grid-cols-7 border-b bg-muted/50 text-center text-xs font-medium uppercase text-muted-foreground">
            {JOURS.map((j) => (
              <div key={j} className="py-2">
                {j}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d, i) => {
              const k = dateKey(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const dayEvents = eventsByDay.get(k) ?? [];
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    openNew(d);
                  }}
                  className={`flex min-h-[92px] flex-col gap-1 border-b border-r p-1.5 text-left ${i % 7 === 6 ? 'border-r-0' : ''} ${inMonth ? '' : 'bg-muted/20 text-muted-foreground'} hover:bg-muted/30`}
                >
                  <span
                    className={`text-xs font-medium ${k === todayKey ? 'flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground' : ''}`}
                  >
                    {d.getDate()}
                  </span>
                  {/* Tous les rendez-vous du jour, sans coupe : la case
                      s'allonge et la ligne de la semaine suit. Le « +5 »
                      discret cachait le gros des journées chargées. */}
                  <div className="space-y-0.5">
                    {dayEvents.map((e) => (
                      <EventChip key={e.id} e={e} jour={k} compact />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : vue === 'semaine' ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-7">
          {days.map((d) => {
            const k = dateKey(d);
            const dayEvents = eventsByDay.get(k) ?? [];
            return (
              <div
                key={k}
                role="button"
                tabIndex={0}
                onClick={() => {
                  openNew(d);
                }}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') openNew(d);
                }}
                className="min-h-[140px] cursor-pointer rounded-lg border bg-card p-2 hover:bg-muted/20"
              >
                <div
                  className={`mb-1.5 text-xs font-semibold ${k === todayKey ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  {d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
                </div>
                {dayEvents.map((e) => (
                  <EventChip key={e.id} e={e} jour={k} />
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border bg-card">
          {(() => {
            const k = dateKey(days[0] ?? cursor);
            const dayEvents = eventsByDay.get(k) ?? [];
            if (dayEvents.length === 0) {
              return (
                <p className="p-8 text-center text-sm text-muted-foreground">
                  Aucun rendez-vous ce jour.
                </p>
              );
            }
            return (
              <div className="divide-y">
                {dayEvents.map((e) => {
                  const type = e.type_id ? typeMap.get(e.type_id) : null;
                  const Icone = iconeForType(type?.icone ?? null);
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => {
                        openEdit(e);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30"
                    >
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
                        style={{ backgroundColor: couleurOf(e) }}
                      >
                        <Icone className="h-4 w-4" />
                      </span>
                      <div className="w-24 shrink-0 text-sm font-medium tabular-nums">
                        {plageDuJour(e, k)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{e.titre}</div>
                        {type?.nom ? (
                          <div className="truncate text-xs text-muted-foreground">
                            {type.nom}
                            {e.adresse ? ` · ${e.adresse}` : ''}
                          </div>
                        ) : e.adresse ? (
                          <div className="truncate text-xs text-muted-foreground">{e.adresse}</div>
                        ) : null}
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {EVENEMENT_STATUT_LABELS[e.statut] ?? e.statut}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {(employesQ.data ?? []).length > 0 ? (
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Intervenants :</span>
          {(employesQ.data ?? []).map((emp) => (
            <span key={emp.id} className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: emp.couleur_agenda ?? '#64748b' }}
              />
              {emp.prenom} {emp.nom}
            </span>
          ))}
        </div>
      ) : null}

      {(equipesQ.data ?? []).length > 0 ? (
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Équipes :</span>
          {(equipesQ.data ?? []).map((eq) => (
            <span key={eq.id} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: eq.couleur }} />
              {eq.nom}
            </span>
          ))}
        </div>
      ) : null}

      {typesQ.data && typesQ.data.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Types :</span>
          {typesQ.data.map((t) => {
            const Icone = iconeForType(t.icone);
            return (
              <span key={t.id} className="flex items-center gap-1.5">
                <Icone className="h-3.5 w-3.5" style={{ color: t.couleur }} />
                {t.nom}
              </span>
            );
          })}
        </div>
      ) : null}

      {form ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            setForm(null);
          }}
        >
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-lg border bg-card p-5"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {form.id ? "Modifier l'événement" : 'Nouvel événement'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Fermer"
                onClick={() => {
                  setForm(null);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {saveMut.isError ? (
              <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {messageErreur(saveMut.error)}
              </div>
            ) : null}

            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="ev_titre">Titre *</Label>
                <Input
                  id="ev_titre"
                  value={form.titre}
                  onChange={(e) => {
                    setForm({ ...form, titre: e.target.value });
                  }}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="ev_type">Type</Label>
                  <Select
                    id="ev_type"
                    value={form.type_id}
                    onChange={(e) => {
                      setForm({ ...form, type_id: e.target.value });
                    }}
                  >
                    <option value="">— Aucun —</option>
                    {(typesQ.data ?? []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nom}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ev_statut">Statut</Label>
                  <Select
                    id="ev_statut"
                    value={form.statut}
                    onChange={(e) => {
                      setForm({ ...form, statut: e.target.value });
                    }}
                  >
                    {Object.entries(EVENEMENT_STATUT_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ev_debut">Début *</Label>
                  <Input
                    id="ev_debut"
                    type="datetime-local"
                    value={form.debut}
                    onChange={(e) => {
                      setForm({ ...form, debut: e.target.value });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ev_fin">Fin *</Label>
                  <Input
                    id="ev_fin"
                    type="datetime-local"
                    value={form.fin}
                    onChange={(e) => {
                      setForm({ ...form, fin: e.target.value });
                    }}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="ev_contact">Client lié</Label>
                <ChoixClient
                  id="ev_contact"
                  clients={contactsQ.data ?? []}
                  chargement={contactsQ.isLoading}
                  value={form.contact_id}
                  labelAucun="— Aucun —"
                  onChange={(v) => {
                    setForm({ ...form, contact_id: v });
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ev_chantier">Chantier lié</Label>
                <Select
                  id="ev_chantier"
                  value={form.chantier_id}
                  onChange={(e) => {
                    setForm({ ...form, chantier_id: e.target.value });
                  }}
                >
                  <option value="">— Aucun —</option>
                  {(chantiersQ.data ?? []).map((ch) => (
                    <option key={ch.id} value={ch.id}>
                      {ch.numero} — {ch.libelle}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="ev_assigne">Assigné à</Label>
                <Select
                  id="ev_assigne"
                  value={form.assigne}
                  onChange={(e) => {
                    setForm({ ...form, assigne: e.target.value });
                  }}
                >
                  <option value="">— Non assigné —</option>
                  {(employesQ.data ?? []).length > 0 ? (
                    <optgroup label="Personnes">
                      {(employesQ.data ?? []).map((emp) => (
                        <option key={emp.id} value={`emp:${emp.id}`}>
                          {emp.prenom} {emp.nom}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                  {(equipesQ.data ?? []).length > 0 ? (
                    <optgroup label="Équipes">
                      {(equipesQ.data ?? []).map((eq) => (
                        <option key={eq.id} value={`eq:${eq.id}`}>
                          {eq.nom}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="ev_adresse">Lieu / adresse</Label>
                <Input
                  id="ev_adresse"
                  value={form.adresse}
                  onChange={(e) => {
                    setForm({ ...form, adresse: e.target.value });
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ev_desc">Notes</Label>
                <Textarea
                  id="ev_desc"
                  value={form.description}
                  onChange={(e) => {
                    setForm({ ...form, description: e.target.value });
                  }}
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between gap-2">
              <div>
                {form.id ? (
                  <Button
                    variant="outline"
                    disabled={deleteMut.isPending}
                    onClick={() => {
                      if (form.id) deleteMut.mutate(form.id);
                    }}
                  >
                    Supprimer
                  </Button>
                ) : null}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setForm(null);
                  }}
                >
                  Annuler
                </Button>
                <Button
                  disabled={saveMut.isPending || form.titre.trim() === ''}
                  onClick={() => {
                    saveMut.mutate(form);
                  }}
                >
                  {saveMut.isPending ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {gestionTypes ? (
        <TypesManager
          onClose={() => {
            setGestionTypes(false);
          }}
        />
      ) : null}

      {gestionEquipes ? (
        <EquipesManager
          onClose={() => {
            setGestionEquipes(false);
          }}
        />
      ) : null}
    </div>
  );
}

// Édition en ligne d'une équipe (couleur + nom) avec compteur de membres.
/** « il y a 3 min », « il y a 2 h », « hier »… pour l'état de synchronisation. */
function depuis(iso: string | null): string {
  if (!iso) return 'jamais';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'à l’instant';
  if (min < 60) return `il y a ${String(min)} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${String(h)} h`;
  return `il y a ${String(Math.round(h / 24))} j`;
}

function EquipeRow({
  equipe,
  membresCount,
  etatSync,
  onSave,
  onDelete,
  saving,
  deleting,
}: {
  equipe: Equipe;
  membresCount: number;
  etatSync: EtatSyncGoogle | null;
  onSave: (nom: string, couleur: string, googleCalendarId: string | null) => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
}) {
  const [nom, setNom] = useState(equipe.nom);
  const [couleur, setCouleur] = useState(equipe.couleur);
  const [gcal, setGcal] = useState(equipe.google_calendar_id ?? '');
  const dirty =
    nom.trim() !== equipe.nom ||
    couleur !== equipe.couleur ||
    gcal.trim() !== (equipe.google_calendar_id ?? '');
  // L'état de synchronisation, en une ligne lisible : en panne, ou à jour.
  let synchro: { texte: string; teinte: string } | null = null;
  if (equipe.google_calendar_id) {
    if (!etatSync) synchro = { texte: 'relié — en attente du premier passage', teinte: 'text-muted-foreground' };
    else if (etatSync.derniere_erreur) synchro = { texte: `en panne (${depuis(etatSync.derniere_execution)}) : ${etatSync.derniere_erreur.slice(0, 120)}`, teinte: 'text-money-out' };
    else synchro = { texte: `synchronisé ${depuis(etatSync.derniere_reussite)} · ${String(etatSync.nb_envoyes)} envoyé(s), ${String(etatSync.nb_recus)} reçu(s) au dernier passage`, teinte: 'text-money-in' };
  }
  return (
    <div className="space-y-2 rounded-md border p-2">
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label="Couleur de l'équipe"
        value={couleur}
        onChange={(e) => {
          setCouleur(e.target.value);
        }}
        className="h-12 w-12 shrink-0 cursor-pointer rounded-md border border-input lg:h-11 lg:w-11"
      />
      <Input
        value={nom}
        onChange={(e) => {
          setNom(e.target.value);
        }}
        className="flex-1"
      />
      <span className="shrink-0 text-xs text-muted-foreground">
        {membresCount} membre{membresCount > 1 ? 's' : ''}
      </span>
      {dirty ? (
        <Button
          size="sm"
          disabled={saving || nom.trim() === ''}
          onClick={() => {
            onSave(nom.trim(), couleur, gcal.trim() || null);
          }}
        >
          Enregistrer
        </Button>
      ) : null}
      <Button
        variant="ghost"
        size="icon"
        aria-label="Supprimer l'équipe"
        disabled={deleting}
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
      <div className="space-y-1">
        <Input
          value={gcal}
          placeholder="Agenda Google : identifiant xxx@group.calendar.google.com (vide = pas de synchronisation)"
          aria-label="Identifiant de l'agenda Google"
          className="font-mono text-xs"
          onChange={(e) => {
            setGcal(e.target.value);
          }}
        />
        {synchro ? <p className={`text-xs ${synchro.teinte}`}>Google : {synchro.texte}</p> : null}
      </div>
    </div>
  );
}

// Modale de gestion des équipes : créer / renommer / recolorer / supprimer une
// équipe, et rattacher chaque intervenant à une équipe.
export function EquipesManager({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const equipesQ = useQuery({ queryKey: ['equipes'], queryFn: listEquipes });
  const membresQ = useQuery({ queryKey: ['membres'], queryFn: listMembres });
  const etatsQ = useQuery({ queryKey: ['gcal-sync-etat'], queryFn: listEtatsSyncGoogle, refetchInterval: 60_000 });
  const etatParAgenda = new Map((etatsQ.data ?? []).map((e) => [e.calendar_id, e]));

  const [nom, setNom] = useState('');
  const [couleur, setCouleur] = useState('#6366f1');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['equipes'] });
    void queryClient.invalidateQueries({ queryKey: ['membres'] });
    void queryClient.invalidateQueries({ queryKey: ['employes'] });
    void queryClient.invalidateQueries({ queryKey: ['evenements'] });
  };

  const createMut = useMutation({
    mutationFn: (v: { nom: string; couleur: string }) => createEquipe(v.nom, v.couleur),
    onSuccess: () => {
      setNom('');
      setCouleur('#6366f1');
      refresh();
    },
  });
  const updateMut = useMutation({
    mutationFn: (v: { id: string; nom: string; couleur: string; gcal: string | null }) =>
      updateEquipe(v.id, v.nom, v.couleur, v.gcal),
    onSuccess: refresh,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteEquipe(id),
    onSuccess: refresh,
  });
  const membreMut = useMutation({
    mutationFn: (v: { employeId: string; equipeId: string | null }) =>
      setEmployeEquipe(v.employeId, v.equipeId),
    onSuccess: refresh,
  });

  const equipes: Equipe[] = equipesQ.data ?? [];
  const membres: EquipeMembre[] = membresQ.data ?? [];
  const echec = createMut.error ?? updateMut.error ?? deleteMut.error ?? membreMut.error ?? null;
  const erreur = echec ? messageErreur(echec) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-lg border bg-card p-5"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Équipes</h2>
          <Button variant="ghost" size="icon" aria-label="Fermer" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mb-4 flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="eq_nom">Nouvelle équipe</Label>
            <Input
              id="eq_nom"
              value={nom}
              placeholder="Ex : Équipe Nord"
              onChange={(e) => {
                setNom(e.target.value);
              }}
            />
          </div>
          <input
            type="color"
            aria-label="Couleur de la nouvelle équipe"
            value={couleur}
            onChange={(e) => {
              setCouleur(e.target.value);
            }}
            className="h-12 w-12 shrink-0 cursor-pointer rounded-md border border-input lg:h-11 lg:w-11"
          />
          <Button
            disabled={nom.trim() === '' || createMut.isPending}
            onClick={() => {
              createMut.mutate({ nom: nom.trim(), couleur });
            }}
          >
            Ajouter
          </Button>
        </div>

        <div className="space-y-2">
          {equipes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune équipe pour le moment.</p>
          ) : (
            equipes.map((eq) => (
              <EquipeRow
                key={eq.id}
                equipe={eq}
                membresCount={membres.filter((m) => m.equipe_id === eq.id).length}
                etatSync={eq.google_calendar_id ? (etatParAgenda.get(eq.google_calendar_id) ?? null) : null}
                saving={updateMut.isPending}
                deleting={deleteMut.isPending}
                onSave={(nomMaj, couleurMaj, gcalMaj) => {
                  updateMut.mutate({ id: eq.id, nom: nomMaj, couleur: couleurMaj, gcal: gcalMaj });
                }}
                onDelete={() => {
                  deleteMut.mutate(eq.id);
                }}
              />
            ))
          )}
        </div>

        {membres.length > 0 ? (
          <div className="mt-5 border-t pt-4">
            <h3 className="mb-2 text-sm font-semibold">Rattacher les intervenants</h3>
            <div className="space-y-2">
              {membres.map((m) => (
                <div key={m.id} className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: m.couleur_agenda ?? '#64748b' }}
                  />
                  <span className="flex-1 text-sm">
                    {m.prenom} {m.nom}
                  </span>
                  <Select
                    className="w-44"
                    value={m.equipe_id ?? ''}
                    onChange={(e) => {
                      membreMut.mutate({
                        employeId: m.id,
                        equipeId: e.target.value === '' ? null : e.target.value,
                      });
                    }}
                  >
                    <option value="">— Sans équipe —</option>
                    {equipes.map((eq) => (
                      <option key={eq.id} value={eq.id}>
                        {eq.nom}
                      </option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {erreur ? <p className="mt-3 text-sm text-destructive">{erreur}</p> : null}
      </div>
    </div>
  );
}

// Sélecteur d'icône-forme (palette de boutons).
function IconePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1">
      {ICONE_KEYS.map((key) => {
        const Icone = iconeForType(key);
        const active = key === value;
        return (
          <button
            key={key}
            type="button"
            aria-label={`Icône ${key}`}
            aria-pressed={active}
            onClick={() => {
              onChange(key);
            }}
            className={`flex h-8 w-8 items-center justify-center rounded border ${
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            <Icone className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}

// Édition en ligne d'un type d'événement (couleur + nom + icône).
function TypeRow({
  type,
  onSave,
  onDelete,
  saving,
  deleting,
}: {
  type: TypeEvenement;
  onSave: (nom: string, couleur: string, icone: string) => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
}) {
  const [nom, setNom] = useState(type.nom);
  const [couleur, setCouleur] = useState(type.couleur);
  const [icone, setIcone] = useState(type.icone ?? 'calendar');
  const dirty =
    nom.trim() !== type.nom || couleur !== type.couleur || icone !== (type.icone ?? 'calendar');
  return (
    <div className="space-y-2 rounded-md border p-2">
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label="Couleur du type"
          value={couleur}
          onChange={(e) => {
            setCouleur(e.target.value);
          }}
          className="h-12 w-12 shrink-0 cursor-pointer rounded-md border border-input lg:h-11 lg:w-11"
        />
        <Input
          value={nom}
          onChange={(e) => {
            setNom(e.target.value);
          }}
          className="flex-1"
        />
        {dirty ? (
          <Button
            size="sm"
            disabled={saving || nom.trim() === ''}
            onClick={() => {
              onSave(nom.trim(), couleur, icone);
            }}
          >
            Enregistrer
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          aria-label="Supprimer le type"
          disabled={deleting}
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <IconePicker value={icone} onChange={setIcone} />
    </div>
  );
}

// Modale d'administration des types d'événement : créer / renommer / recolorer
// / choisir l'icône / supprimer. La couleur et la forme alimentent l'agenda.
function TypesManager({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const typesQ = useQuery({ queryKey: ['types_evenement'], queryFn: listTypesEvenement });

  const [nom, setNom] = useState('');
  const [couleur, setCouleur] = useState('#1D4595');
  const [icone, setIcone] = useState('calendar');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['types_evenement'] });
    void queryClient.invalidateQueries({ queryKey: ['evenements'] });
  };

  const types: TypeEvenement[] = typesQ.data ?? [];
  const nextOrdre = types.reduce((m, t) => Math.max(m, t.ordre), 0) + 1;

  const createMut = useMutation({
    mutationFn: (v: { nom: string; couleur: string; icone: string; ordre: number }) =>
      createTypeEvenement(v),
    onSuccess: () => {
      setNom('');
      setCouleur('#1D4595');
      setIcone('calendar');
      refresh();
    },
  });
  const updateMut = useMutation({
    mutationFn: (v: { id: string; nom: string; couleur: string; icone: string }) =>
      updateTypeEvenement(v.id, { nom: v.nom, couleur: v.couleur, icone: v.icone }),
    onSuccess: refresh,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteTypeEvenement(id),
    onSuccess: refresh,
  });

  const echec = createMut.error ?? updateMut.error ?? deleteMut.error ?? null;
  const erreur = echec ? messageErreur(echec) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-lg border bg-card p-5"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Types d&apos;événement</h2>
          <Button variant="ghost" size="icon" aria-label="Fermer" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mb-4 space-y-2 rounded-md border border-dashed p-2">
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label="Couleur du nouveau type"
              value={couleur}
              onChange={(e) => {
                setCouleur(e.target.value);
              }}
              className="h-12 w-12 shrink-0 cursor-pointer rounded-md border border-input lg:h-11 lg:w-11"
            />
            <Input
              value={nom}
              placeholder="Nouveau type (ex : Réunion)"
              onChange={(e) => {
                setNom(e.target.value);
              }}
              className="flex-1"
            />
            <Button
              disabled={nom.trim() === '' || createMut.isPending}
              onClick={() => {
                createMut.mutate({ nom: nom.trim(), couleur, icone, ordre: nextOrdre });
              }}
            >
              Ajouter
            </Button>
          </div>
          <IconePicker value={icone} onChange={setIcone} />
        </div>

        <div className="space-y-2">
          {types.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun type pour le moment.</p>
          ) : (
            types.map((t) => (
              <TypeRow
                key={t.id}
                type={t}
                saving={updateMut.isPending}
                deleting={deleteMut.isPending}
                onSave={(nomMaj, couleurMaj, iconeMaj) => {
                  updateMut.mutate({ id: t.id, nom: nomMaj, couleur: couleurMaj, icone: iconeMaj });
                }}
                onDelete={() => {
                  deleteMut.mutate(t.id);
                }}
              />
            ))
          )}
        </div>

        {erreur ? <p className="mt-3 text-sm text-destructive">{erreur}</p> : null}
      </div>
    </div>
  );
}
